import type { ReactNode } from 'react';

import { Box, Link } from '@mui/material';
import { captureException } from '@sentry/react';
import { colors, LogoIcon, Page, PageContent } from '@truvhq/ui';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useHistory } from 'react-router-dom';

import { StatusPageInfo } from 'app/integrations/StatusPageInfo';
import sidePanelDashboardImage from 'assets/images/side-panel-dashboard.png';
import { AnalyticsButton } from 'components/AnalyticsButton';
import { useAuth } from 'shared/lib/auth/useAuth';
import { useStatusPageIncident } from 'shared/lib/statusPage/useStatusPageIncident';

type AuthLayoutTemplateProps = {
  children: ReactNode;
  showLogo?: boolean;
  logoWidth?: number;
  pageName?: string;
};

export const AuthLayoutTemplate = ({
  children,
  showLogo = true,
  logoWidth = 54,
  pageName = '',
}: AuthLayoutTemplateProps) => {
  const { logout } = useAuth();
  const history = useHistory();
  const { data: statusPageIncident } = useStatusPageIncident();

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      history.push('/');
    } catch (error) {
      captureException(error);
    }
  };

  return (
    <Page className="flex h-full flex-col">
      {statusPageIncident && (
        <div className="sticky top-0 w-full">
          <StatusPageInfo incident={statusPageIncident} />
        </div>
      )}
      <PageContent fullscreen className="flex h-full">
        <div className="flex-1 px-8">
          <PerfectScrollbar className="flex flex-col">
            <Box
              sx={{
                my: '32px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {showLogo && <LogoIcon width={logoWidth} />}
              <AnalyticsButton
                className="text-grey100"
                color="inherit"
                eventName={`${pageName} - click logout`}
                onClick={handleLogout}
              >
                <span>Log out</span>
              </AnalyticsButton>
            </Box>
            <div className="flex flex-grow flex-col items-center justify-center">
              <div className="w-full max-w-[440px]">{children}</div>
            </div>
            <div className="flex flex-row justify-center gap-6 m-8">
              <Link href="https://truv.com/privacy" fontSize={14} color="text.primary" target="_blank">
                Privacy Policy
              </Link>
              <Link href="https://truv.com/terms" fontSize={14} color="text.primary" target="_blank">
                Terms
              </Link>
            </div>
          </PerfectScrollbar>
        </div>

        <Box
          sx={{
            display: { xs: 'none', lg: 'flex' },
            flex: 1,
            backgroundColor: colors.grey20,
            maxWidth: 600,
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              marginY: 'auto',
            }}
          >
            <img
              src={sidePanelDashboardImage}
              alt="Truv app preview"
              style={{
                height: '670px',
              }}
            />
          </Box>
        </Box>
      </PageContent>
    </Page>
  );
};
